import { DwEnvironmentInfo } from '@devwareapps/devware-cap';
import { EnvironmentNames } from '../environments/models/environment-names.enum';
import { Injectable, Injector } from '@angular/core';
import { DwCoreConfig, DwAuthGuardService, DwConfig, DwUIMetaDataConfigDefaults, DwConfigServiceToken, DwUIConfig, DwAgGridComponent, DwUIMetaDataConfig, DwConfigProviderBase, DwUiConfigName, DwAttributeType, DwControlType, DwMediaConfigName, DwMediaConfig, DwFormDebugService, DwLogSeverity, DwSecurityState } from '@devwareapps/devware-cap';
import { environment } from 'src/environments/environment';
import { angularApplicationVersion } from './app-version';
import { AppPermissions } from './meta-data/app-permissions.enum';
import { EnvironmentConfigItem } from '../environments/models/environment-config.model';
import { Router } from '@angular/router';
import { DwContainerCollapseStyle, DwUiContainerStyleReference, DwUiConfigInitDecorator, DwUiConfigRegistryService, DwUiRegistryType } from '@devwareapps/devware-cap';

@DwConfig()
@Injectable()
export class AppDevwareConfigProviderService extends DwConfigProviderBase {
    private static readonly enhancedDefaultContainerStyle: DwUiContainerStyleReference = {
        keyName: '',
        display: 'Default',
        containerClass: 'dw-form-section-container-enhanced',
        containerInnerClass: 'dw-form-section-container-enhanced-inner',
        containerTitleClass: 'dw-form-section-container-enhanced-header'
    };

    environmentConfigItem: EnvironmentConfigItem;

    constructor(
        private injector: Injector,
        private router: Router
    ) {
        super();
        // TODO: Add event replicators here when fitness entity metadata is available
    }

    configureCoreConfig(dwCoreConfig: DwCoreConfig) {
        dwCoreConfig.applicationVersion = angularApplicationVersion;
        dwCoreConfig.baseTitle = AppDevwareConfigProviderService.getEnvironmentBaseTitle();

        this.environmentConfigItem = AppDevwareConfigProviderService.getEnvironmentConfigItem();
        if (!this.environmentConfigItem) {
            console.error('No environment config item found');
            return;
        }

        dwCoreConfig.apiRoot = this.environmentConfigItem.apiRoot;

        dwCoreConfig.alternateApiRoots = (environment as any).alternateApiRoots as any;
        dwCoreConfig.tenantKey = (environment as any).tenantKey;

        if (environment.environmentMessage == 'Dev') {
            DwFormDebugService.showAllMessages = false;
            DwFormDebugService.componentNameList = [];
            DwFormDebugService.disableLogging = true;
        }

        // Configure logging
        dwCoreConfig.enableConsoleLogging = (environment as any).enabledConsoleLogging;
        dwCoreConfig.loggingConfig.writeAllToConsole = (environment as any).enableConsoleLogging;

        dwCoreConfig.loggingConfig.serverMessagePrefix = 'Fitness Angular';
        dwCoreConfig.loggingConfig.writeToServerLevels = [DwLogSeverity.error];

        dwCoreConfig.loggingConfig.logSlowApiCalls.elapsedTimeThresholdMs = 30000;

        // Override the default CanActivate guard
        dwCoreConfig.routingConfig.defaultCanActivateGuards = [DwAuthGuardService];
        dwCoreConfig.routingConfig.googleAnalyticsTrackingToken = environment.googleAnalyticsTrackingToken;

        // Setup security anonymous access
        dwCoreConfig.securityConfig.allowAnonymousAccess = true;
        dwCoreConfig.securityConfig.useRefreshTokens = true;
        dwCoreConfig.securityConfig.refreshSessionStorage = 'localStorage';
        dwCoreConfig.securityConfig.clientSessionName = 'Holiday Streakers browser';
        dwCoreConfig.securityConfig.securityTokenTimeoutMinutes = 5;
        dwCoreConfig.securityConfig.autoLoginAfterRegistration = true;

        dwCoreConfig.routingConfig.defaultRouteUrl = '';

        dwCoreConfig.autoUpdateConfig.updateMessageTooltip = 'Refresh your browser to get the latest version of the app';

        if (!environment.disabledUserInactvityTimeout) {
            dwCoreConfig.securityConfig.userInactivitiy = {
                warningTimeoutMinutes: 25,
                warningMessage: 'You will be logged out in 5 minutes due to inactivity.',
                logoutTimeoutMinutes: 30,
                logoutMessage: 'You have been logged out due to inactivity.',
            };
        }
    }

    configureAdditionalConfig(configName: string, config: any, dwCoreConfig: DwCoreConfig) {
        switch (configName) {
            case DwUiConfigName:
                this.configureUIConfig(config, dwCoreConfig);
                break;

            case DwUIMetaDataConfigDefaults.configName:
                this.configureUIMetaDataConfig(config);
                break;

            case DwMediaConfigName:
                this.configureMediaConfig(config, dwCoreConfig);
                break;
        }
    }

    private configureUIConfig(dwUiConfig: DwUIConfig, dwCoreConfig: DwCoreConfig) {
        dwUiConfig.showContactUs = environment.showContactUs;
        dwUiConfig.contactUsEMail = environment.contactUsEmail;

        dwUiConfig.gridComponent = DwAgGridComponent;
        dwUiConfig.pageFooterHeight = 0;
        dwUiConfig.showFooter = false;

        dwUiConfig.allowConfiguration = environment.allowConfiguration;

        dwCoreConfig.globals.apiRoot = dwCoreConfig.apiRoot;
        dwCoreConfig.globals.defaultAvatar = dwUiConfig.defaultAvatarImageUrl;
        // Imported activity records retain legacy prod-group containers; their
        // binaries still live on the old site until the media migration completes.
        dwCoreConfig.globals.legacyActivityPhotoBaseUrl = 'https://app.holidaystreakers.com/Streakers/Media/';

        dwUiConfig.defaultBackgroundImageUrl = 'assets/img/brand/default_banner.png';
        dwUiConfig.emailValidationRegEx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,50})+$/;

        // Customize the registration process
        dwUiConfig.registrationConfig = {
            registerTitle: 'Sign up for Holiday Streakers',
            registerButtonText: 'Sign up',
            registrationComponentKey: 'fitness-user-registration-extension',
            useFloatLabelOnRegistration: true,
            registrationHideUserName: true,
            ...dwUiConfig.registrationConfig,
            registrationShowAsLink: true,
            registrationLinkText: "Don't have an account?",
            useFloatLabelOnLogin: true,
        };

        dwUiConfig.enableThemes = true;

        dwUiConfig.securityInfoConfig = {
            showWelcome: false,
            getNameDisplay: (user, emulating, config, nameOnly) => {
                if (!user) {
                    return '';
                }

                let currentUserDisplay = user.FirstNameLastName;

                if (!nameOnly) {
                    if (config.showWelcome) {
                        return 'Welcome ' + currentUserDisplay;
                    }
                    if (emulating) {
                        currentUserDisplay = user.FirstNameLastName;
                        return 'Emulating ' + currentUserDisplay;
                    }
                }

                return currentUserDisplay;
            },
            getAdditionalLine: () => ''
        };
    }

    private configureUIMetaDataConfig(dwUiMetaDataConfig: DwUIMetaDataConfig) {
        dwUiMetaDataConfig.defaultContainerStyle = {
            ...AppDevwareConfigProviderService.enhancedDefaultContainerStyle
        };
        dwUiMetaDataConfig.defaultContainerCollapseStyle = DwContainerCollapseStyle.chevronRight;
        dwUiMetaDataConfig.useGmtDateTime = true;
        dwUiMetaDataConfig.use24HourTime = false;
        dwUiMetaDataConfig.pdfRenderWaitTimeMs = 1200;
        dwUiMetaDataConfig.emailValidationRegEx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,50})+$/;

        dwUiMetaDataConfig.gridDefaultPreferences.rowHeight = 25;

        dwUiMetaDataConfig.userContextExtendedAttributes = [
            { name: 'UserTypeId', display: 'User Type', attrType: DwAttributeType.Integer, controlType: DwControlType.DropDownList, lookupId: 0 }
        ];

        dwUiMetaDataConfig.formAdvancedSettings = {
            variableContext: {
                formUpdateDelayMs: -1,
                mergeFullContext: true,
                contextUpdateDelayMs: -1,
                contextUpdateMergeFullContext: true,
                logContextChanges: true
            }
        };
    }

    @DwUiConfigInitDecorator({ name: 'FitnessUiConfigInit', useDI: true })
    setupRegistrations(): void {
        // CAP registers the unnamed default separately from metadata defaults.
        this.injector.get(DwUiConfigRegistryService).registerItem(
            DwUiRegistryType.containerStyles,
            { ...AppDevwareConfigProviderService.enhancedDefaultContainerStyle }
        );
    }

    configureMediaConfig(config: DwMediaConfig, dwCoreConfig: DwCoreConfig) {
        config.defaultMaxFileSizeMb = 50;

        config.cdnSettings = {
            useCDN: this.environmentConfigItem?.useCDN && !!this.environmentConfigItem?.cdnRoot,
            cdnUrl: this.environmentConfigItem?.cdnRoot,
        };
    }

    public static getEnvironmentConfigItem(): EnvironmentConfigItem {
        const mainEnvironment = environment.environment;
        const altEnvironment = (environment as any).altEnvironment;

        if (!altEnvironment) {
            return mainEnvironment;
        }

        if (altEnvironment.hostNameMatch) {
            const hostName = window.location.hostname;

            if (hostName == altEnvironment.hostNameMatch) {
                return altEnvironment;
            }
        }

        return mainEnvironment;
    }
    public static getEnvironmentBaseTitle(): string {
        return AppDevwareConfigProviderService.getEnvironmentBaseTitleForRuntime();
    }

    public static getEnvironmentBaseTitleForRuntime(environmentInfo?: DwEnvironmentInfo): string {
        const appTitle = (environment.baseTitle || '').trim();
        const environmentConfigItem = AppDevwareConfigProviderService.getEnvironmentConfigItem();

        if (!environmentConfigItem || environmentConfigItem.envKey === EnvironmentNames.prod) {
            return appTitle;
        }

        if (AppDevwareConfigProviderService.isLocalEnvironment(environmentConfigItem)) {
            return AppDevwareConfigProviderService.getLocalEnvironmentBaseTitle(appTitle, environmentConfigItem, environmentInfo);
        }

        return AppDevwareConfigProviderService.buildEnvironmentTitle(
            appTitle,
            environmentConfigItem.environmentEmoji,
            environmentConfigItem.environmentLabel
        );
    }

    private static getLocalEnvironmentBaseTitle(appTitle: string, environmentConfigItem: EnvironmentConfigItem, environmentInfo?: DwEnvironmentInfo): string {
        const runtimeEnvironment = AppDevwareConfigProviderService.getRuntimeEnvironmentDisplayInfo(environmentInfo, environmentConfigItem);

        if (!runtimeEnvironment) {
            return AppDevwareConfigProviderService.buildEnvironmentTitle(
                appTitle,
                environmentConfigItem.environmentEmoji,
                environmentConfigItem.environmentLabel
            );
        }

        if (runtimeEnvironment.label === 'LOCAL') {
            return AppDevwareConfigProviderService.buildEnvironmentTitle(
                appTitle,
                environmentConfigItem.environmentEmoji,
                'LOCAL UI/API',
                '◆◆'
            );
        }

        return AppDevwareConfigProviderService.buildEnvironmentTitle(
            appTitle,
            runtimeEnvironment.emoji,
            `LOCAL -> ${runtimeEnvironment.label}`,
            '✦'
        );
    }

    private static buildEnvironmentTitle(appTitle: string, environmentEmoji?: string, environmentLabel?: string, separatorSymbol?: string): string {
        return [(environmentEmoji || '').trim(), (separatorSymbol || '').trim(), appTitle, environmentLabel ? `[${environmentLabel.trim()}]` : '']
            .filter(x => !!x)
            .join(' ')
            .trim();
    }

    private static isLocalEnvironment(environmentConfigItem: EnvironmentConfigItem): boolean {
        return environmentConfigItem?.envKey === EnvironmentNames.local
            || environmentConfigItem?.envKey === EnvironmentNames.enterprise
            || AppDevwareConfigProviderService.isLocalApiRoot(environmentConfigItem?.apiRoot);
    }

    private static isLocalApiRoot(apiRoot: string): boolean {
        return !!apiRoot && /localhost|127\.0\.0\.1/i.test(apiRoot);
    }

    private static getRuntimeEnvironmentDisplayInfo(environmentInfo?: DwEnvironmentInfo, environmentConfigItem?: EnvironmentConfigItem): { label: string, emoji: string } {
        const environmentName = `${environmentInfo?.EnvironmentName || ''}`.trim().toLowerCase();

        if (environmentName.includes('local')) {
            return { label: 'LOCAL', emoji: '🟢' };
        }

        if (environmentName.includes('stage')) {
            return { label: 'STAGE', emoji: '🟡' };
        }

        if (environmentName.includes('test')) {
            return { label: 'TEST', emoji: '🟠' };
        }

        if (environmentName.includes('feature') || environmentName.includes('dev')) {
            return { label: 'DEV', emoji: '🔵' };
        }

        if ((environmentInfo as any)?.IsProd) {
            return { label: 'PROD', emoji: '' };
        }

        if ((environmentInfo as any)?.IsTest) {
            return { label: 'TEST', emoji: '🟠' };
        }

        if ((environmentInfo as any)?.IsDev) {
            return { label: 'DEV', emoji: '🔵' };
        }

        if (AppDevwareConfigProviderService.isLocalApiRoot(environmentConfigItem?.apiRoot)) {
            return { label: 'LOCAL', emoji: '🟢' };
        }

        return null;
    }
}
